import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { clearActiveCountEndpointUnavailable } from '@/lib/activeCountAvailability';
import { clearArchivedCountEndpointUnavailable } from '@/lib/archivedCountAvailability';
import { mergeApplicationsById } from '@/lib/bulkProcessApplications';
import { queryKeys } from '@/lib/queryKeys';
import type { Application, JobPosition } from '@/types';

/** Coalesce tab badge refetches during rapid list patches (bulk poll, queue reconcile). */
export const TAB_COUNT_INVALIDATE_DEBOUNCE_MS = 2_000;

const tabCountInvalidateTimers = new Map<string, ReturnType<typeof setTimeout>>();

function invalidateTabCountsNow(queryClient: QueryClient, companyId: string): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.applicationsActiveCount(companyId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.applicationsArchivedCount(companyId),
  });
  void queryClient.invalidateQueries({
    queryKey: ['applications-filtered-count', companyId],
  });
}

function scheduleInvalidateTabCounts(queryClient: QueryClient, companyId: string): void {
  const prev = tabCountInvalidateTimers.get(companyId);
  if (prev !== undefined) clearTimeout(prev);
  tabCountInvalidateTimers.set(
    companyId,
    setTimeout(() => {
      tabCountInvalidateTimers.delete(companyId);
      invalidateTabCountsNow(queryClient, companyId);
    }, TAB_COUNT_INVALIDATE_DEBOUNCE_MS),
  );
}

/** Flush pending debounced tab-count invalidation (e.g. after archive). */
export function flushInvalidateTabCounts(queryClient: QueryClient, companyId: string): void {
  const prev = tabCountInvalidateTimers.get(companyId);
  if (prev !== undefined) {
    clearTimeout(prev);
    tabCountInvalidateTimers.delete(companyId);
  }
  invalidateTabCountsNow(queryClient, companyId);
}

/** Merge updates into existing rows and append rows that are not in the list yet. */
function upsertApplicationsList(prev: Application[], updates: Application[]): Application[] {
  if (updates.length === 0) return prev;
  const merged = mergeApplicationsById(prev, updates);
  const present = new Set(merged.map((a) => a.id));
  const appended = updates.filter((u) => !present.has(u.id));
  return appended.length === 0 ? merged : [...merged, ...appended];
}

export function resolveApplicationPositionId(
  app: Pick<Application, 'positionId'> & { jobPositionId?: string },
): string {
  return app.positionId || app.jobPositionId || '';
}

type CompanyApplicationsPage = {
  items: Application[];
  hasMore: boolean;
  nextCursor?: string;
};

function patchInfiniteListPages(
  queryClient: QueryClient,
  companyId: string,
  updates: Application[],
): void {
  queryClient.setQueriesData<InfiniteData<CompanyApplicationsPage>>(
    { queryKey: ['applications-list', companyId] },
    (old) => {
      if (!old?.pages?.length) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          items: upsertApplicationsList(page.items, updates),
        })),
      };
    },
  );
}

function patchPositionCaches(
  queryClient: QueryClient,
  companyId: string,
  apps: Application[],
  archived: boolean,
): void {
  const byPosition = new Map<string, Application[]>();
  for (const app of apps) {
    const positionId = resolveApplicationPositionId(app);
    if (!positionId) continue;
    const list = byPosition.get(positionId) ?? [];
    list.push(app);
    byPosition.set(positionId, list);
  }
  for (const [positionId, rows] of byPosition) {
    queryClient.setQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, archived),
      (prev = []) => upsertApplicationsList(prev, rows),
    );
  }
}

/**
 * Single write path for application list React Query caches (company + per-position).
 * Pages and hooks should prefer this over direct setQueryData on list keys.
 */
export class ApplicationListSync {
  constructor(private readonly queryClient: QueryClient) {}

  getCompanyList(companyId: string, archived = false): Application[] | undefined {
    return this.queryClient.getQueryData<Application[]>(
      queryKeys.applications(companyId, archived),
    );
  }

  getPositionList(
    companyId: string,
    positionId: string,
    archived = false,
  ): Application[] | undefined {
    return this.queryClient.getQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, archived),
    );
  }

  /** Merge rows into company cache and every touched position cache. */
  patchMany(companyId: string, updates: Application[], archived = false): void {
    if (updates.length === 0) return;
    this.queryClient.setQueryData<Application[]>(
      queryKeys.applications(companyId, archived),
      (prev = []) => upsertApplicationsList(prev, updates),
    );
    patchPositionCaches(this.queryClient, companyId, updates, archived);
    patchInfiniteListPages(this.queryClient, companyId, updates);
    scheduleInvalidateTabCounts(this.queryClient, companyId);
  }

  /** Union legacy company list + all `applications-list` infinite pages (deduped by id). */
  getFlattenedListApplications(companyId: string, archived = false): Application[] {
    const byId = new Map<string, Application>();
    for (const app of this.getCompanyList(companyId, archived) ?? []) {
      byId.set(app.id, app);
    }
    const entries = this.queryClient.getQueriesData<InfiniteData<CompanyApplicationsPage>>({
      queryKey: ['applications-list', companyId],
    });
    for (const [, data] of entries) {
      if (!data?.pages?.length) continue;
      for (const app of data.pages.flatMap((p) => p.items)) {
        byId.set(app.id, app);
      }
    }
    return Array.from(byId.values());
  }

  /** Functional update across company list, infinite list pages, and touched position caches. */
  patchCompanyUpdater(
    companyId: string,
    updater: (prev: Application[]) => Application[],
    archived = false,
  ): void {
    const prev = this.getFlattenedListApplications(companyId, archived);
    const next = updater(prev);
    const nextById = new Map(next.map((a) => [a.id, a]));

    this.queryClient.setQueryData<Application[]>(
      queryKeys.applications(companyId, archived),
      next,
    );

    const seenInPages = new Set<string>();
    this.queryClient.setQueriesData<InfiniteData<CompanyApplicationsPage>>(
      { queryKey: ['applications-list', companyId] },
      (old) => {
        if (!old?.pages?.length) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((a) => {
              seenInPages.add(a.id);
              return nextById.get(a.id) ?? a;
            }),
          })),
        };
      },
    );

    const additions = next.filter((a) => !seenInPages.has(a.id));
    if (additions.length > 0) {
      patchInfiniteListPages(this.queryClient, companyId, additions);
    }

    patchPositionCaches(this.queryClient, companyId, next, archived);
    scheduleInvalidateTabCounts(this.queryClient, companyId);
  }

  private applicationExistsInCompanyCaches(companyId: string, applicationId: string): boolean {
    const legacy = this.getCompanyList(companyId, false) ?? [];
    if (legacy.some((a) => a.id === applicationId)) return true;
    const entries = this.queryClient.getQueriesData<InfiniteData<CompanyApplicationsPage>>({
      queryKey: ['applications-list', companyId],
    });
    for (const [, data] of entries) {
      if (!data?.pages?.length) continue;
      for (const page of data.pages) {
        if (page.items.some((a) => a.id === applicationId)) return true;
      }
    }
    return false;
  }

  /** Refetch tab badge totals immediately (archive flow, upsert fallback). */
  invalidateTabCounts(companyId: string): void {
    flushInvalidateTabCounts(this.queryClient, companyId);
  }

  bumpActiveCount(companyId: string, delta: number): void {
    this.queryClient.setQueryData(
      queryKeys.applicationsActiveCount(companyId),
      (count: number | undefined) => Math.max(0, (count ?? 0) + delta),
    );
    clearActiveCountEndpointUnavailable(companyId);
  }

  /** Fetch one application and upsert into active company + position caches. */
  async upsertOne(
    companyId: string,
    positionId: string,
    applicationId: string,
  ): Promise<void> {
    const application = await api.applications.get(companyId, positionId, applicationId);
    if (!application) {
      await this.invalidateCompany(companyId, false);
      scheduleInvalidateTabCounts(this.queryClient, companyId);
      return;
    }
    const existed = this.applicationExistsInCompanyCaches(companyId, application.id);
    const archived = application.archived === true;
    this.patchMany(companyId, [application], archived);
    if (!archived && !existed) {
      this.bumpActiveCount(companyId, 1);
    }
  }

  /**
   * Position tab showed empty cached [] while company or network may still have rows.
   * Triggers a background refetch when the cached slice is empty.
   */
  ensurePositionListFresh(
    companyId: string,
    positionId: string,
    archived = false,
  ): void {
    const cached = this.getPositionList(companyId, positionId, archived);
    if (cached && cached.length > 0) return;
    void this.queryClient.refetchQueries({
      queryKey: queryKeys.positionApplications(companyId, positionId, archived),
      type: 'active',
    });
  }

  async refetchCompany(companyId: string, archived = false): Promise<void> {
    await this.queryClient.refetchQueries({
      queryKey: queryKeys.applications(companyId, archived),
      type: 'active',
    });
  }

  async refetchAllCompanyScopes(companyId: string): Promise<void> {
    await this.queryClient.refetchQueries({
      queryKey: ['applications', companyId],
      type: 'active',
    });
    await this.queryClient.refetchQueries({
      queryKey: ['applications-list', companyId],
      type: 'active',
    });
  }

  async invalidateCompany(companyId: string, archived?: boolean): Promise<void> {
    if (archived === undefined) {
      await this.queryClient.invalidateQueries({ queryKey: ['applications', companyId] });
      await this.queryClient.invalidateQueries({ queryKey: ['applications-list', companyId] });
      return;
    }
    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.applications(companyId, archived),
    });
  }

  async invalidatePosition(
    companyId: string,
    positionId: string,
    archived?: boolean,
  ): Promise<void> {
    if (archived === undefined) {
      await this.queryClient.invalidateQueries({
        queryKey: queryKeys.positionApplications(companyId, positionId),
      });
      return;
    }
    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.positionApplications(companyId, positionId, archived),
    });
  }

  /** Defer archived list invalidation so optimistic rows are not wiped by a lagging BE page. */
  scheduleArchiveInvalidation(companyId: string): void {
    void this.queryClient.invalidateQueries({
      queryKey: queryKeys.applications(companyId, false),
    });
    void this.queryClient.invalidateQueries({
      queryKey: ['applications-list', companyId],
    });
    void this.queryClient.invalidateQueries({
      queryKey: ['applications-filtered-count', companyId],
    });
    this.invalidateTabCounts(companyId);
    window.setTimeout(() => {
      void this.queryClient.invalidateQueries({
        queryKey: queryKeys.applications(companyId, true),
      });
    }, 800);
  }

  bumpArchivedCount(companyId: string, delta: number): void {
    this.queryClient.setQueryData(
      queryKeys.applicationsArchivedCount(companyId),
      (count: number | undefined) => Math.max(0, (count ?? 0) + delta),
    );
    clearArchivedCountEndpointUnavailable(companyId);
  }
}

export function createApplicationListSync(queryClient: QueryClient): ApplicationListSync {
  return new ApplicationListSync(queryClient);
}

/**
 * Fetches all company application pages, writing each page into the React Query cache
 * so the table and tab counts update incrementally instead of after one long request.
 */
export async function hydrateCompanyApplicationsProgressive(
  queryClient: QueryClient,
  companyId: string,
  positions: JobPosition[],
  archived = false,
): Promise<Application[]> {
  const key = queryKeys.applications(companyId, archived);
  let accumulated: Application[] = queryClient.getQueryData(key) ?? [];
  let cursor: string | undefined;
  let hasMore = true;
  let safety = 150;

  while (hasMore && safety-- > 0) {
    const page = await api.applications.listByCompany(companyId, positions, {
      limit: 100,
      cursor,
      archived,
    });
    accumulated = upsertApplicationsList(accumulated, page.items);
    queryClient.setQueryData(key, accumulated);
    hasMore = page.hasMore;
    cursor = page.nextCursor;
  }

  return accumulated;
}
